import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IconButton, Tooltip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { deleteSite, getSites } from '../../../../../api/endpoints/cmsSite';
import type { Site } from '../../../../../api/types/cmsSite';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../../components/common';
import { SiteFormDialog } from '../SiteFormDialog';

/** Site CMS hiyerarşisinin kökü — 11 alt kaynağı `SiteDetailPage`'de sekme olarak yönetilir. */
export default function SiteListPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);
  const [search, setSearch] = useState('');

  const query = useQuery({ queryKey: ['cms', 'sites'], queryFn: () => getSites() });
  const rows: Site[] = useMemo(() => query.data?.sites ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.company, row.address, row.tursab, row.iata]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteSite(id),
    invalidateKeys: [['cms', 'sites']],
    successMessage: 'Site silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.company ?? '';

  const columns: DataTableColumn<Site>[] = [
    { id: 'company', header: 'Firma', cell: (r) => r.company ?? '—' },
    { id: 'address', header: 'Adres', cell: (r) => r.address ?? '—' },
    { id: 'tursab', header: 'TURSAB', cell: (r) => r.tursab ?? '—' },
    { id: 'iata', header: 'IATA', cell: (r) => r.iata ?? '—' },
  ];

  return (
    <ListPageScaffold
      title="Site Ayarları"
      createLabel="Yeni Site"
      onCreate={() => setCreateOpen(true)}
      search={{ value: search, onChange: setSearch, placeholder: 'Firma, adres, TURSAB, IATA...' }}
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={rows.length === 0 ? 'Kayıtlı site bulunamadı.' : 'Aramayla eşleşen site bulunamadı.'}
      rowActions={(r) => (
        <RowActions
          onEdit={() => setEditing(r)}
          onDelete={() => del.request(r.id)}
          leading={
            <Tooltip title="Yönet (Header, Slider, İletişim, Şehirler, Kampanyalar…)">
              <IconButton size="small" onClick={() => navigate(`/cms/site/${r.id}`)} aria-label="Yönet">
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
      )}
    >
      <SiteFormDialog open={createOpen} site={null} onClose={() => setCreateOpen(false)} />
      <SiteFormDialog open={!!editing} site={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Siteyi sil"
        description={`"${deletingName}" sitesini silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
